import PageHeader from '../../components/layout/PageHeader';
import { useFirestoreAllArrayContains, useFirestoreNoCompagnyOrderBy, useFirestoreGeneral, useFirestoreLoaded } from '../../firebase/useFirestore';
import { client } from '../../hooks/Client';
import { Link } from 'react-router-dom';
import { useState, useMemo, useEffect, useCallback, useContext } from 'react';
import firebase from '../../firebase/compat';
import { db, timestamp } from '../../firebase/config';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import spinner from '../../images/spinner-ripple.svg';
import SelectableGrid from '../../components/common/SelectableGrid';
import { daysSince, businessDaysSince, daysColorClass, formatDayAge } from '../../utils/activityAge';
import Table from '../../components/common/table/Table.jsx';
import TableCell from '../../components/common/table/TableCell.jsx';
import useRetentionScoresBulk from '../../hooks/retention/useRetentionScoresBulk';
import ReadOnlyGuard from '../../components/common/ReadOnlyGuard';
import SuperAdminSessionModal from '../../components/superAdmin/SuperAdminSessionModal';
import { Auth } from '../../StateManagment/Auth';
import effectiveProjectStatus from '../../helpers/effectiveProjectStatus';
import useAvailableSectors from '../../hooks/useAvailableSectors';
import filterProjectsBySector from '../../helpers/portfolio/filterProjectsBySector';
import portfolioRowSearchText from '../../helpers/portfolio/portfolioRowSearchText';
import buildSectorTabs from '../../helpers/portfolio/buildSectorTabs';
import ProjectSectorsCell from '../../components/portfolio/ProjectSectorsCell';
import useOverviewCounts from '../../hooks/portfolio/useOverviewCounts';
import ProjectMetricValue from '../../components/portfolio/ProjectMetricValue';
import { TOC_METRICS, sectorMetricsFor } from '../../helpers/portfolio/projectMetricDefinitions';
import { getSectorInfo } from '../../helpers/meetstandaard/sectorCatalog';
import StarToggle from '../../components/common/StarToggle';
import MultiSelect from '../../components/common/MultiSelect';
import ProjectLabelFilter from '../../components/portfolio/ProjectLabelFilter';
import ProjectLabelChip from '../../components/portfolio/ProjectLabelChip';
import useProjectNotesColumn from '../../hooks/portfolio/useProjectNotesColumn';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { isStarred, starredPatch } from '../../helpers/starred/starred';
import { resolveProjectLabels } from '../../helpers/adressHelpers/resolveAdressLabels';
import { projectMarkDocId, projectMarkBase, indexProjectMarks, filterProjectsByMarks, projectLabelLookup, visibleLabelKeys } from '../../helpers/portfolio/projectMarks';

const SUPER_ADMIN = '123423HJKHVKH'

// De gedeelde tabel zet een kolom vast met deze twee classes; de breedte staat
// als variabele in CSS/layout/table.css. Ook in gebruik door de
// aanwezigheidstabel (components/personas/Dashboard/PresenceTotals.jsx).
const STICKY_HEAD = 'common-table__head-cell--sticky-1'
const STICKY_CELL = 'common-table__cell--sticky-1'

// Buiten de component: een nieuwe functie-identiteit per render laat de tabel
// bij elke toetsaanslag opnieuw filteren.
const projectSearchText = (item) => item?.CommunityName || ''

const ProjectsOverview = () => {
    const isSuperAdmin = client === SUPER_ADMIN

    const [selectedStatus, setSelectedStatus] = useState('active')
    // Sectorfilter: 'all' | 'none' | een sector-slug uit CompagnyMeta.SelectedSectors
    const [selectedSector, setSelectedSector] = useState('all')
    // De ruwe Users-documenten van de gekozen status; de zichtbare adreslijst
    // wordt daar in het geheugen uit afgeleid.
    const [statusUsers, setStatusUsers] = useState([])
    const [emailLoading, setEmailLoading] = useState(false)
    const [collapsedPortfolios, setCollapsedPortfolios] = useState({})
    // Tenant waarvoor een superadmin-sessie wordt aangevraagd (null = geen modal).
    const [sessionTarget, setSessionTarget] = useState(null)

    const [authO] = useContext(Auth)
    const [updateError, setUpdateError] = useState(null)
    // Wat de zoekbalk van de tabel overlaat. De zoekterm woont in de tabel zelf,
    // dus zonder deze melding belooft de CSV eronder 'deze selectie' terwijl er
    // drie rijen in beeld staan.
    // AI-CONTEXT: See docs/pitfalls.md#ui-export-claims-everything-on-screen
    const [searchVisibleProjects, setSearchVisibleProjects] = useState(null)
    // De filters 'Met ster' en 'Label' ('' = alle labels), zoals bij huishoudens.
    const [starOnly, setStarOnly] = useState(false)
    const [labelKey, setLabelKey] = useState('')

    const host = window.location.hostname

    // Kiesbare sectoren komen uit dezelfde bron als de sectorkiezer in
    // Instellingen → Algemeen, zodat de twee lijsten niet uit elkaar lopen.
    const { selectableSectors } = useAvailableSectors()

    // TOC-tellingen komen van de server: de beheertenant mag de TOC-collecties
    // van andere organisaties niet via de Firestore-regels lezen. Een portfolio-
    // beheerder valt buiten die gate, dus voor die krijgt de tabel de kolommen
    // niet — beter geen kolom dan een kolom die stil 0 toont.
    const { countsByCompagnyId: tocByCompagnyId, error: tocError } = useOverviewCounts('getTocOverview', isSuperAdmin)
    const showTocColumns = isSuperAdmin

    // De sectorkolommen horen bij één sector: huishoudens en materialen zeggen
    // niets over een arbeidsparticipatie-organisatie en andersom. Ze verschijnen
    // dus pas zodra er op die sector gefilterd wordt — en dan wordt de tweede
    // serverfunctie ook pas aangeroepen. Welke kolommen dat zijn staat in
    // projectMetricDefinitions.js, gedeeld met de projectdetailpagina.
    const sectorMetrics = showTocColumns ? sectorMetricsFor(selectedSector) : []
    const showSectorColumns = sectorMetrics.length > 0

    const { countsByCompagnyId: activityByCompagnyId, error: activityError } =
        useOverviewCounts('getSectorActivityOverview', showSectorColumns)

    const allProjectsRaw = useFirestoreNoCompagnyOrderBy(isSuperAdmin ? 'CompagnyMeta' : null, 'Timestamp', 'desc') || []
    const childProjects = useFirestoreAllArrayContains('CompagnyMeta', 'Parent', client)
    const ownMetaDocs = useFirestoreGeneral('CompagnyMeta', 'CompagnyID', client)
    const ownMeta = Array.isArray(ownMetaDocs) && ownMetaDocs.length > 0 ? ownMetaDocs[0] : null

    const isPortfolioBeheerder = !isSuperAdmin && ownMeta?.Portfolio === true

    // Sterren en labels zijn van de KIJKER: ProjectMarks met deze omgeving als
    // CompagnyID, niet een veld op het project. Zo heeft Deccos-beheer en elk
    // portfolio zijn eigen markeringen (keuze Gijs 2026-10-16).
    // AI-CONTEXT: See src/helpers/portfolio/projectMarks.js
    const [projectMarks, marksLoaded] = useFirestoreLoaded('ProjectMarks')
    const marksByProject = useMemo(() => indexProjectMarks(projectMarks), [projectMarks])
    const markFilters = useMemo(() => ({ starOnly, labelKey }), [starOnly, labelKey])
    const marksFilterActive = starOnly || labelKey !== ''

    // De labels zelf (naam, kleur) staan in Instellingen → Portfolio.
    const projectLabels = useMemo(() => resolveProjectLabels(ownMeta), [ownMeta])
    const { options: labelOptions, byKey: labelByKey } = useMemo(() => projectLabelLookup(projectLabels), [projectLabels])

    // Kolom NOTITIES met '+ Notitie' — alleen de beheeromgeving heeft
    // projectnotities, dus elders wordt er niets gelezen.
    const { renderNoteCell: renderNotes, noteModal } = useProjectNotesColumn(isSuperAdmin)

    // Demo portfolio accounts: hun demo-projecten tellen als actief in deze views
    const showDemoAsActive = ownMeta?.ShowDemoProjects === true

    const projects = isSuperAdmin ? allProjectsRaw : (childProjects || [])

    const allProjects = useMemo(
        () => [...projects].sort((a, b) => (a.CommunityName || '').localeCompare(b.CommunityName || '')),
        [projects]
    )

    // Scores berekenen we alleen voor actieve orgs — bespaart Firestore-reads
    // en bij inactieve/gearchiveerde projecten zegt een score sowieso weinig.
    const activeProjects = useMemo(
        () => allProjects.filter(p => effectiveProjectStatus(p, showDemoAsActive) === 'active'),
        [allProjects, showDemoAsActive]
    )

    const { scoresByEntityId, loading: scoresLoading } = useRetentionScoresBulk(activeProjects)

    const tabs = isPortfolioBeheerder
        ? [
            { value: 'active', label: 'Actief' },
            { value: 'inactive', label: 'Inactief' },
        ]
        : [
            { value: 'active', label: 'Actief' },
            { value: 'inactive', label: 'Inactief' },
            { value: 'archived', label: 'Gearchiveerd' },
            { value: 'demo', label: 'Demo' },
            { value: 'all', label: 'Alle' },
            { value: 'none', label: 'Geen status' },
            { value: 'portfolio', label: 'Portfolio' },
        ]

    const statusCounts = useMemo(() => {
        const counts = { all: allProjects.length, active: 0, inactive: 0, archived: 0, demo: 0, none: 0, portfolio: 0 }
        for (const p of allProjects) {
            const s = effectiveProjectStatus(p, showDemoAsActive).trim()
            if (!s) counts.none++
            else if (s === 'active') counts.active++
            else if (s === 'inactive') counts.inactive++
            else if (s === 'archived') counts.archived++
            else if (s === 'demo') counts.demo++
            if (p.Portfolio === true) counts.portfolio++
        }
        return counts
    }, [allProjects, showDemoAsActive])

    const projectsForStatus = useMemo(() => {
        if (selectedStatus === 'all') return allProjects
        if (selectedStatus === 'none') return allProjects.filter(p => !p.Status)
        if (selectedStatus === 'portfolio') return allProjects.filter(p => p.Portfolio === true)
        return allProjects.filter(p => effectiveProjectStatus(p, showDemoAsActive) === selectedStatus)
    }, [allProjects, selectedStatus, showDemoAsActive])

    // Op het portfolio-tabblad toont de tabel de PROJECTEN onder elk portfolio,
    // niet de portfolio's zelf — en daar filtert de sectorkeuze dus ook op. De
    // knoppen moeten dezelfde groep tellen, anders staat er 'Energiearmoede (0)'
    // boven een groep met veertien energiearmoede-projecten.
    const sectorCountSource = useMemo(() => {
        if (selectedStatus !== 'portfolio') return projectsForStatus

        const portfolioIds = new Set(
            allProjects.filter(p => p.Portfolio === true).map(p => p.CompagnyID)
        )
        return allProjects.filter(p =>
            Array.isArray(p.Parent) && p.Parent.some(id => portfolioIds.has(id))
        )
    }, [allProjects, projectsForStatus, selectedStatus])

    // De sectorknoppen tellen binnen de gekozen status — anders belooft een
    // telling meer rijen dan de tabel eronder laat zien.
    const sectorTabs = useMemo(
        () => buildSectorTabs(sectorCountSource, selectableSectors),
        [sectorCountSource, selectableSectors]
    )

    // Ster en label filteren BINNEN status en sector; de knoppen erboven
    // tellen dus wat er zonder deze filters is, net als bij huishoudens.
    const filteredProjects = useMemo(
        () => filterProjectsByMarks(filterProjectsBySector(projectsForStatus, selectedSector), marksByProject, markFilters),
        [projectsForStatus, selectedSector, marksByProject, markFilters]
    )

    const projectById = useMemo(() => {
        const map = new Map()
        for (const p of allProjects) if (p.CompagnyID) map.set(p.CompagnyID, p)
        return map
    }, [allProjects])

    const portfolioGroups = useMemo(() => {
        const portfolios = allProjects.filter(p => p.Portfolio === true)
        const groups = portfolios.map(portfolio => ({
            portfolio,
            children: filterProjectsByMarks(
                filterProjectsBySector(
                    allProjects.filter(p =>
                        Array.isArray(p.Parent) && p.Parent.includes(portfolio.CompagnyID)
                    ),
                    selectedSector
                ),
                marksByProject,
                markFilters
            ),
        }))

        // Bij een actief filter zegt een portfolio zonder overgebleven
        // projecten niets; zonder filter blijven lege portfolio's wel staan.
        return selectedSector === 'all' && !marksFilterActive ? groups : groups.filter(g => g.children.length > 0)
    }, [allProjects, selectedSector, marksByProject, markFilters, marksFilterActive])

    const portfolioTableItems = useMemo(
        () =>
            portfolioGroups.flatMap(({ portfolio, children }) => {
                const isCollapsed = collapsedPortfolios[portfolio.CompagnyID] !== false

                return [
                    {
                        kind: 'group',
                        portfolio,
                        children,
                        isCollapsed,
                    },
                    ...(!isCollapsed
                        ? children.map((child) => ({
                            kind: 'child',
                            item: child,
                            portfolio,
                        }))
                        : []),
                ]
            }),
        [portfolioGroups, collapsedPortfolios]
    )

    // De kopregels die de zoekterm overlaat. De CSV eronder telt in deze
    // weergave de portfolio's zelf, dus de kindrijen blijven buiten beschouwing.
    const reportVisiblePortfolios = useCallback(
        (rows) => setSearchVisibleProjects(
            rows.filter((row) => row.kind === 'group').map((row) => row.portfolio)
        ),
        []
    )

    const togglePortfolio = (id) =>
        setCollapsedPortfolios(prev => ({ ...prev, [id]: !(prev[id] ?? true) }))

    const updateStatus = async (item, value) => {
        const docId = item?.docid
        if (!docId) {
            // eslint-disable-next-line no-console
            console.error('Failed to update status: no document ID found on item', item)
            setUpdateError('Bijwerken van status is mislukt: geen geldig document-ID gevonden.')
            return
        }
        try {
            // Cross-tenant write: Firestore-rules blokkeren dit client-side, dus
            // via de super-admin-gegate Cloud Function (europe-west1).
            const updateProjectStatus = firebase.app().functions('europe-west1').httpsCallable('updateProjectStatus')
            await updateProjectStatus({ docId, status: value })
            setUpdateError(null)
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to update status:', err)
            setUpdateError(`Bijwerken van status is mislukt: ${err.message}`)
        }
    }

    // Sectoren zetten vanuit het overzicht. Zelfde cross-tenant-situatie als de
    // status: de Firestore-regel op CompagnyMeta laat de beheertenant niet
    // client-side schrijven, dus dit loopt via een super-admin-gegate functie.
    // Stabiele referentie: ProjectSectorsCell is gememoiseerd, en een nieuwe
    // functie per render zou die memo elke keer weer ongeldig maken.
    const updateSectors = useCallback(async (docId, sectors) => {
        try {
            const updateProjectSectors = firebase.app().functions('europe-west1').httpsCallable('updateProjectSectors')
            await updateProjectSectors({ docId, sectors })
            setUpdateError(null)
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to update sectors:', err)
            setUpdateError(`Bijwerken van de sector is mislukt: ${err.message}`)
        }
    }, [])

    // Ster en labels schrijven in het EIGEN ProjectMarks-document van deze
    // omgeving: één per project (vaste id), aangemaakt bij de eerste schrijfactie.
    // Labels met arrayUnion/arrayRemove, zodat twee snelle klikken elkaar niet
    // overschrijven.
    /**
     * @param {{CompagnyID: string}} item - Het project
     * @param {object} patch - De velden naast de vaste basis
     * @param {string} wat - Voor de foutmelding
     */
    const writeMark = async (item, patch, wat) => {
        try {
            await db.collection('ProjectMarks')
                .doc(projectMarkDocId(client, item.CompagnyID))
                .set({ ...projectMarkBase(client, item.CompagnyID, timestamp), ...patch }, { merge: true })
            setUpdateError(null)
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`Failed to update ${wat}:`, err)
            setUpdateError(`Bijwerken van ${wat} is mislukt: ${err.message}`)
        }
    }

    /** @param {{CompagnyID: string}} item @param {boolean} next */
    const starHandler = (item, next) => writeMark(item, starredPatch(next, authO?.ID, timestamp), 'de ster')

    /**
     * @param {{CompagnyID: string}} item
     * @param {string} key - De labelsleutel
     * @param {boolean} isSelected - De HUIDIGE stand, zoals MultiSelect hem meegeeft
     */
    const labelHandler = (item, key, isSelected) => writeMark(item, {
        Labels: isSelected
            ? firebase.firestore.FieldValue.arrayRemove(key)
            : firebase.firestore.FieldValue.arrayUnion(key),
    }, 'het label')

    const chunk = (arr, size) => {
        const out = []
        for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
        return out
    }

    const csvEscape = (v) => {
        const s = String(v ?? '')
        return `"${s.replaceAll('"', '""')}"`
    }

    const buildCsv = (rows) => {
        const headers = ['Email', 'FirstName']
        const lines = [headers.map(csvEscape).join(',')]
        for (const r of rows) {
            lines.push([csvEscape(r.Email), csvEscape(r.FirstName)].join(','))
        }
        return lines.join('\n')
    }

    const downloadCsv = () => {
        if (!statusEmailList?.length) return
        const csv = buildCsv(statusEmailList)
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `deccos-users-${selectedStatus || 'all'}.csv`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    // Stabiele dep voor het CSV-fetch effect: alleen herfetchen als de SET project-IDs
    // werkelijk wijzigt, niet bij elke nieuwe array-referentie (anders: oneindige
    // loop door scoresByEntityId-updates → nieuwe filter → nieuwe ref).
    //
    // Bewust op de STATUS-lijst en niet op de sector-gefilterde lijst: het
    // sectorfilter is een keuze binnen wat hier al opgehaald is, en meesturen zou
    // elke sectorklik de hele Users-fetch laten overdoen. De sector wordt er
    // hieronder in het geheugen uitgefilterd.
    // AI-CONTEXT: See docs/pitfalls.md#dev-reused-name-repoints-consumers
    const statusIdsKey = useMemo(
        () => projectsForStatus.map(p => p.CompagnyID).filter(Boolean).sort().join(','),
        [projectsForStatus]
    )

    useEffect(() => {
        if (!isSuperAdmin) return

        let cancelled = false
        setEmailLoading(true)

        ;(async () => {
            const projectIds = statusIdsKey ? statusIdsKey.split(',') : []

            if (projectIds.length === 0) {
                if (!cancelled) { setStatusUsers([]); setEmailLoading(false) }
                return
            }

            // Parallel, niet in een lus: bij 250 projecten zijn dit 25 query's, en
            // seriëel wachten maakt van een tabklik een wachttijd van tientallen
            // seconden. Zelfde aanpak als useRetentionScoresBulk.
            const results = await Promise.all(
                chunk(projectIds, 10).map(ids =>
                    db.collection('Users')
                        .where('Compagny', 'array-contains-any', ids)
                        .get()
                        .then(qs => qs.docs.map(doc => doc.data()))
                        .catch(error => {
                            // eslint-disable-next-line no-console
                            console.error('Error fetching users: ', error)
                            return []
                        })
                )
            )

            if (!cancelled) {
                setStatusUsers(results.flat())
                setEmailLoading(false)
            }
        })()

        return () => { cancelled = true }
    }, [statusIdsKey, isSuperAdmin])

    // De organisaties die de CSV eronder belooft: status, sector én zoekterm.
    // De doorsnede met `filteredProjects` houdt de betekenis gelijk aan die van
    // de knoppenrijen — in de gegroepeerde weergave zijn dat de portfolio's
    // zelf, precies zoals vóór de zoekbalk.
    const emailProjects = useMemo(() => {
        if (!searchVisibleProjects) return filteredProjects

        const searchIds = new Set(searchVisibleProjects.map(p => p?.CompagnyID).filter(Boolean))
        return filteredProjects.filter(p => searchIds.has(p.CompagnyID))
    }, [filteredProjects, searchVisibleProjects])

    // De unieke adressen binnen de zichtbare selectie. Een gebruiker kan bij
    // meerdere organisaties horen, dus de match loopt over zijn hele Compagny-array.
    const statusEmailList = useMemo(() => {
        const visibleIds = new Set(emailProjects.map(p => p.CompagnyID).filter(Boolean))
        const byEmail = new Map()

        statusUsers.forEach(user => {
            const compagny = Array.isArray(user.Compagny) ? user.Compagny : []
            if (!compagny.some(id => visibleIds.has(id))) return

            const email = (user.Email || '').trim()
            if (!email) return

            const key = email.toLowerCase()
            if (!byEmail.has(key)) byEmail.set(key, { Email: email, FirstName: user.ForName || '' })
        })

        return Array.from(byEmail.values())
    }, [statusUsers, emailProjects])

    // De kolommen komen uit dezelfde lijst als de kaarten op de
    // projectdetailpagina; alleen de schrijfwijze verschilt, want een tabelkop
    // staat hier in kapitalen.
    // AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
    const columnsFor = (metrics) =>
        metrics.map((metric) => ({ key: metric.key, label: metric.label.toUpperCase(), searchable: false }))

    // ACTIVITEITEN staat twee keer in de tabel — één keer als uitgevoerde
    // activiteiten (gebruik) en één keer als activiteiten in de Theory of
    // Change. De groepskoppen erboven houden die twee uit elkaar.
    const tocColumns = columnsFor(TOC_METRICS)

    // Kolommen van de gefilterde sector. Contextinfo hoort hier en niet bij de
    // Theory of Change: het zijn organisatiekenmerken van die ene sector.
    const sectorColumns = columnsFor(sectorMetrics)

    const usageColumns = [
        { key: 'events', label: 'ACTIVITEITEN', searchable: false },
        { key: 'results', label: 'ONDERZOEKEN', searchable: false },
    ]

    const labelsColumn = { key: 'labels', label: 'LABELS', searchable: false }
    const notesColumns = isSuperAdmin ? [{ key: 'notes', label: 'NOTITIES', searchable: false }] : []

    // De eerste kolom blijft staan tijdens zijwaarts scrollen. Deze tabel is met
    // de TOC- en sectorkolommen ruim breder dan het scherm, en zonder de naam
    // in beeld weet je bij kolom 12 niet meer welk project je leest.
    const regularColumns = [
        { key: 'name', label: 'NAAM', headerClassName: STICKY_HEAD },
        { key: 'portfolio', label: 'PORTFOLIO', searchable: false },
        { key: 'sector', label: 'SECTOR', searchable: false },
        { key: 'status', label: 'STATUS', searchable: false },
        labelsColumn,
        ...notesColumns,
        { key: 'login', label: 'LAATSTE LOGIN', searchable: false },
        ...usageColumns,
        ...(showTocColumns ? tocColumns : []),
        ...sectorColumns,
        { key: 'environment', label: 'PROJECT OMGEVING', searchable: false },
    ]

    const portfolioColumns = [
        { key: 'project', label: 'PORTFOLIO / PROJECT', headerClassName: STICKY_HEAD },
        { key: 'sector', label: 'SECTOR', searchable: false },
        { key: 'status', label: 'STATUS', searchable: false },
        labelsColumn,
        ...notesColumns,
        { key: 'login', label: 'LAATSTE LOGIN', searchable: false },
        ...usageColumns,
        ...(showTocColumns ? tocColumns : []),
        ...sectorColumns,
        { key: 'environment', label: 'PROJECT OMGEVING', searchable: false },
    ]

    // Twee koprijen zodra de TOC-kolommen meedoen: zonder de groepskoppen staat
    // ACTIVITEITEN twee keer naast elkaar zonder dat iets zegt wat het verschil
    // is. Zonder TOC-kolommen blijft de kop één rij — dan valt er niets te
    // groeperen.
    const headerRowsFor = (columns) => {
        if (!showTocColumns) return null

        const leading = columns.slice(0, columns.findIndex((c) => c.key === 'events'))

        return [
            [
                ...leading.map((column) => ({ ...column, rowSpan: 2 })),
                { key: 'group-usage', label: 'GEBRUIK', colSpan: usageColumns.length },
                { key: 'group-toc', label: 'THEORY OF CHANGE', colSpan: tocColumns.length },
                ...(sectorColumns.length > 0
                    ? [{
                        key: 'group-sector',
                        label: getSectorInfo(selectedSector).label.toUpperCase(),
                        colSpan: sectorColumns.length,
                    }]
                    : []),
                { key: 'environment', label: 'PROJECT OMGEVING', rowSpan: 2 },
            ],
            [...usageColumns, ...tocColumns, ...sectorColumns],
        ]
    }

    const renderLastLogin = (item) => {
        const d = businessDaysSince(item.LastLogin)

        return d !== null
            ? <span className={daysColorClass(d)}>{formatDayAge(d, 'wd')}</span>
            : '—'
    }

    // Activity / research counts derived from bulk-fetched signals (no per-row listeners).
    // Window: 16 weken voor activiteiten, 12 maanden voor onderzoek.
    const renderSignalCount = (item, countKey, lastDateKey) => {
        const score = scoresByEntityId.get(item.CompagnyID)
        if (scoresLoading && !score) return '…'
        const count = score?.signals?.[countKey] ?? 0
        const lastDate = score?.signals?.[lastDateKey] ?? null
        const d = lastDate ? daysSince({ toDate: () => lastDate }) : null
        return (
            <span>
                {count}
                {d !== null && <span className={daysColorClass(d)}> ({formatDayAge(d)})</span>}
            </span>
        )
    }

    // Eén cel per meetwaarde, in dezelfde volgorde als de kolommen. Wat er in
    // de cel komt te staan — een getal, een verhouding, '…' zolang de
    // tellingen onderweg zijn of '?' na een fout — bepaalt ProjectMetricValue,
    // gedeeld met de kaarten op de projectdetailpagina.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const renderMetricCells = (item, metrics, byCompagnyId, error) => metrics.map((metric) => (
        <TableCell key={metric.key}>
            <ProjectMetricValue
                metric={metric}
                byCompagnyId={byCompagnyId}
                compagnyId={item.CompagnyID}
                failed={Boolean(error)}
                project={item}
            />
        </TableCell>
    ))

    const renderSectorCells = (item) =>
        renderMetricCells(item, sectorMetrics, activityByCompagnyId, activityError)

    const renderTocCells = (item) =>
        renderMetricCells(item, TOC_METRICS, tocByCompagnyId, tocError)

    // Sectoren wijzigen loopt via een super-admin-gegate functie; een portfolio-
    // beheerder ziet ze wel, maar kan ze niet zetten.
    const renderSectors = (item) => (
        <ProjectSectorsCell
            project={item}
            selectableSectors={selectableSectors}
            canEdit={isSuperAdmin}
            onChange={updateSectors}
        />
    )

    // De ster staat in de vaste naamkolom, zodat hij bij zijwaarts scrollen in
    // beeld blijft. Zonder bewerkrecht schakelt de fieldset van ReadOnlyGuard hem uit.
    const renderName = (item, className) => (
        <div className="projects-overview-name">
            <StarToggle
                starred={isStarred(marksByProject.get(item.CompagnyID))}
                onToggle={(next) => starHandler(item, next)}
            />
            <Link className={className} to={`/${client}/portfolioprojectdetails/${item.CompagnyID}`}>
                {item.CommunityName || '—'}
            </Link>
        </div>
    )

    // Dezelfde chip-kiezer als de labels bij huishoudens. Een label dat de
    // organisatie verborgen heeft blijft op het project staan, maar is hier
    // niet te zien of te kiezen.
    const renderLabels = (item) => (
        <MultiSelect
            selected={visibleLabelKeys(marksByProject.get(item.CompagnyID), labelByKey)}
            options={labelOptions}
            onToggle={(key, isSelected) => labelHandler(item, key, isSelected)}
            renderSelected={(key) => <ProjectLabelChip label={labelByKey[key]} labelKey={key} />}
            loaded={marksLoaded}
            addLabel="+ Label"
            emptyLabel="—"
            emptyOptionsLabel="Geen labels ingesteld (Instellingen → Portfolio)"
            removeAriaLabel="Label verwijderen"
        />
    )

    // Zolang de markeringen laden is 'niets gevonden' bij een actief ster- of
    // labelfilter nog niet waar.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const marksPending = marksFilterActive && !marksLoaded

    const filterBar = (
        <>
            <ProjectLabelFilter options={labelOptions} value={labelKey} onChange={setLabelKey} />
            <button
                type="button"
                className={`star-filter${starOnly ? ' star-filter--on' : ''}`}
                aria-pressed={starOnly}
                aria-label="Met ster"
                title="Met ster"
                onClick={() => setStarOnly((aan) => !aan)}
            >
                <StarRoundedIcon fontSize="small" />
            </button>
            {marksFilterActive && (
                <button
                    type="button"
                    className="table-filter-clear"
                    onClick={() => { setStarOnly(false); setLabelKey('') }}
                >
                    Wis filters
                </button>
            )}
        </>
    )

    const tenantUrl = (compagnyId) => (
        host === 'localhost'
            ? `http://${host}:3001/${compagnyId}/home`
            : `https://${host}/${compagnyId}/home`
    )

    // Ben ik teamlid van deze tenant? Zo niet, dan heeft een superadmin een
    // tijdelijke sessie nodig — anders is de omgeving leeg.
    // AI-CONTEXT: See docs/features/superadmin-tenant-access.md
    const myCompagnies = Array.isArray(authO?.Compagny) ? authO.Compagny : []
    const canOpenAsSuperAdmin = authO?.SuperAdmin === true

    const renderProjectLink = (item) => (
        <div className="superadmin-open-tenant-cell">
            <a href={tenantUrl(item.CompagnyID)} target="_blank" rel="noreferrer">
                <LaunchOutlinedIcon />
            </a>
            {canOpenAsSuperAdmin && !myCompagnies.includes(item.CompagnyID) && (
                <button
                    className="superadmin-open-tenant-button"
                    onClick={() => setSessionTarget({
                        compagnyId: item.CompagnyID,
                        name: item.CommunityName || item.CompagnyID,
                    })}
                >
                    sessie starten
                </button>
            )}
        </div>
    )

    return (
        <div className="main">
            <div className="main-container">
                <PageHeader
                    heading={'Projecten overzicht'}
                    description={'Hier vind je een overzicht van alle projecten'}
                    AI={false}
                />

                <SelectableGrid
                    items={tabs}
                    getId={(item) => item.value}
                    getLabel={(item) => `${item.label} (${statusCounts[item.value] ?? 0})`}
                    selectedId={selectedStatus}
                    onClick={setSelectedStatus}
                />

                <SelectableGrid
                    items={sectorTabs}
                    getId={(item) => item.value}
                    getLabel={(item) => `${item.label} (${item.projectCount})`}
                    selectedId={selectedSector}
                    onClick={setSelectedSector}
                />

                {updateError && (
                    <div className="projects-overview-error" role="alert">
                        {updateError}
                        <button
                            type="button"
                            className="projects-overview-error__dismiss"
                            onClick={() => setUpdateError(null)}
                            aria-label="Sluiten"
                        >×</button>
                    </div>
                )}

                {tocError && (
                    <div className="projects-overview-error" role="alert">
                        De TOC-tellingen konden niet opgehaald worden: {tocError}. Die kolommen
                        tonen daarom een vraagteken; het is dus niet gezegd dat er niets is.
                    </div>
                )}

                {activityError && (
                    <div className="projects-overview-error" role="alert">
                        De registratietellingen konden niet opgehaald worden: {activityError}. Die
                        kolommen tonen daarom een vraagteken; het is dus niet gezegd dat er niets is.
                    </div>
                )}

                {selectedStatus === 'portfolio' ? (
                    <ReadOnlyGuard edit="portfolio.edit">
                    <div className="table-container">
                        <Table
                            columns={portfolioColumns}
                            headerRows={headerRowsFor(portfolioColumns)}
                            tableClassName="projects-overview-table"
                            items={portfolioTableItems}
                            getItemKey={(row) =>
                                row.kind === 'group'
                                    ? `group-${row.portfolio.CompagnyID}`
                                    : row.item.docid || row.item.docId || row.item.ID || row.item.CompagnyID
                            }
                            searchAccessor={portfolioRowSearchText}
                            onFilteredItemsChange={reportVisiblePortfolios}
                            searchPlaceholder="Zoeken op portfolio of project"
                            searchBarStart={filterBar}
                            searchSummary={(shown, total) => `${shown} van ${total} rijen`}
                            emptyMessage={marksPending ? 'Sterren en labels worden geladen…' : "Geen portfolio's gevonden."}
                            getRowClassName={(row) =>
                                row.kind === 'group' ? 'portfolio-group-header-row' : 'portfolio-child-row'
                            }
                            renderRow={(row) => (
                                row.kind === 'group' ? (
                                    <TableCell colSpan={portfolioColumns.length} className="portfolio-group-cell">
                                        <div className="portfolio-group-header" onClick={() => togglePortfolio(row.portfolio.CompagnyID)}>
                                            <span className="portfolio-group-toggle">{row.isCollapsed ? '▶' : '▼'}</span>
                                            <span className="portfolio-group-name">{row.portfolio.CommunityName || '—'}</span>
                                            <span className="portfolio-group-count">{row.children.length} projecten</span>
                                        </div>
                                    </TableCell>
                                ) : (
                                    <>
                                        <TableCell className={STICKY_CELL}>
                                            {renderName(row.item, 'portfolio-child-name')}
                                        </TableCell>
                                        <TableCell>{renderSectors(row.item)}</TableCell>
                                        <TableCell>
                                            <select
                                                value={row.item.Status || ''}
                                                onChange={(e) => updateStatus(row.item, e.target.value)}
                                            >
                                                <option value="">—</option>
                                                <option value="active">Actief</option>
                                                <option value="inactive">Inactief</option>
                                                <option value="archived">Gearchiveerd</option>
                                                <option value="demo">Demo</option>
                                            </select>
                                        </TableCell>
                                        <TableCell>{renderLabels(row.item)}</TableCell>
                                        {isSuperAdmin && <TableCell>{renderNotes(row.item)}</TableCell>}
                                        <TableCell>{renderLastLogin(row.item)}</TableCell>
                                        <TableCell>{renderSignalCount(row.item, 'eventsCountInWindow', 'lastEventDate')}</TableCell>
                                        <TableCell>{renderSignalCount(row.item, 'researchesCountInWindow', 'lastResearchDate')}</TableCell>
                                        {showTocColumns && renderTocCells(row.item)}
                                        {renderSectorCells(row.item)}
                                        <TableCell>{renderProjectLink(row.item)}</TableCell>
                                    </>
                                )
                            )}
                        />
                    </div>
                    </ReadOnlyGuard>
                ) : (
                    <ReadOnlyGuard edit="portfolio.edit">
                    <div className="table-container">
                        <Table
                            columns={regularColumns}
                            headerRows={headerRowsFor(regularColumns)}
                            tableClassName="projects-overview-table"
                            items={filteredProjects}
                            getItemKey={(item) => item.docid || item.docId || item.ID || item.CompagnyID}
                            searchAccessor={projectSearchText}
                            onFilteredItemsChange={setSearchVisibleProjects}
                            searchPlaceholder="Zoeken op organisatie"
                            searchBarStart={filterBar}
                            searchSummary={(shown, total) => `${shown} van ${total} organisaties`}
                            emptyMessage={marksPending ? 'Sterren en labels worden geladen…' : 'Geen projecten voor deze selectie.'}
                            renderRow={(item) => {
                                const parents = Array.isArray(item.Parent)
                                    ? item.Parent
                                        .map(id => ({ id, name: projectById.get(id)?.CommunityName }))
                                        .filter(p => p.name)
                                    : []

                                return (
                                    <>
                                        <TableCell className={STICKY_CELL}>
                                            {renderName(item)}
                                        </TableCell>
                                        <TableCell>
                                            <div className="portfolio-tags">
                                                {parents.map(parent => (
                                                    <span
                                                        key={parent.id}
                                                        className="portfolio-tag"
                                                        onClick={() => setSelectedStatus('portfolio')}
                                                    >
                                                        {parent.name}
                                                    </span>
                                                ))}
                                            </div>
                                        </TableCell>
                                        <TableCell>{renderSectors(item)}</TableCell>
                                        <TableCell>
                                            <select
                                                value={item.Status || ''}
                                                onChange={(e) => updateStatus(item, e.target.value)}
                                            >
                                                <option value="">—</option>
                                                <option value="active">Actief</option>
                                                <option value="inactive">Inactief</option>
                                                <option value="archived">Gearchiveerd</option>
                                                <option value="demo">Demo</option>
                                            </select>
                                        </TableCell>
                                        <TableCell>{renderLabels(item)}</TableCell>
                                        {isSuperAdmin && <TableCell>{renderNotes(item)}</TableCell>}
                                        <TableCell>{renderLastLogin(item)}</TableCell>
                                        <TableCell>{renderSignalCount(item, 'eventsCountInWindow', 'lastEventDate')}</TableCell>
                                        <TableCell>{renderSignalCount(item, 'researchesCountInWindow', 'lastResearchDate')}</TableCell>
                                        {showTocColumns && renderTocCells(item)}
                                        {renderSectorCells(item)}
                                        <TableCell>{renderProjectLink(item)}</TableCell>
                                    </>
                                )
                            }}
                        />
                    </div>
                    </ReadOnlyGuard>
                )}

                {isSuperAdmin && (
                    <div className="projects-overview-csv-block">
                        {emailLoading ? (
                            <img className="projects-overview-csv-spinner" src={spinner} alt="Laden..." />
                        ) : statusEmailList.length > 0 && (
                            <div>
                                <h3>Unieke emailadressen ({statusEmailList.length}) van gebruikers in deze selectie:</h3>
                                <button onClick={downloadCsv}>Download CSV</button>
                            </div>
                        )}
                    </div>
                )}

                {noteModal}

                {sessionTarget && (
                    <SuperAdminSessionModal
                        modalOpen={true}
                        setModalOpen={() => setSessionTarget(null)}
                        compagnyId={sessionTarget.compagnyId}
                        tenantName={sessionTarget.name}
                        onStarted={() => {
                            window.open(tenantUrl(sessionTarget.compagnyId), '_blank', 'noreferrer')
                            setSessionTarget(null)
                        }}
                    />
                )}
            </div>
        </div>
    )
}

export default ProjectsOverview
